<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
  } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    DEFAULT_CODEPAGE,
    IDestinationSelection,
    IUnresolvedV1Assignment,
    applyAssignmentSelection,
  } from '@/stores/printing-v2.config';
  import { useV2PrintingUi } from '@/composition/printing/use-printing-v2-ui';
  import { DEFAULT_ESCPOS_SETTINGS } from '@/lib/print/printer-settings';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2WizardSteps from './V2WizardSteps.vue';
  import StepConnection, { TWizardConnection } from './StepConnection.vue';
  import StepDevice, { IWizardDevice } from './StepDevice.vue';
  import StepPaper, { IWizardPaperChoice } from './StepPaper.vue';
  import StepTest from './StepTest.vue';
  import StepAssign, { IWizardAssignResult } from './StepAssign.vue';

  const props = withDefaults(
    defineProps<{
      /** Unresolved-v1 reassign flow: pre-checks that destination on finish. */
      preTarget?: IUnresolvedV1Assignment | null;
    }>(),
    { preTarget: null }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const ui = useUi();
  const { v2Store, bridge, printingStore } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // #region wizard state

  const STEPS = ['connection', 'device', 'paper', 'test', 'assign'] as const;
  type TStep = typeof STEPS[number];

  const stepIndex = ref(0);
  const currentStep = computed<TStep>(() => STEPS[stepIndex.value]);

  const stepLabels = computed(() => [
    i18nT('printingV2.stepConnection'),
    i18nT('printingV2.stepDevice'),
    i18nT('printingV2.stepPaper'),
    i18nT('printingV2.stepTest'),
    i18nT('printingV2.stepAssign'),
  ]);

  const connection = ref<TWizardConnection | null>(null);
  const device = ref<IWizardDevice | null>(null);
  const paper = ref<IWizardPaperChoice | null>(null);
  const codepage = ref(DEFAULT_CODEPAGE);
  const finishing = ref(false);
  let finished = false;

  const preSelection = computed<IDestinationSelection[]>(() =>
    props.preTarget
      ? [
          {
            destination: props.preTarget.destination,
            stationId: props.preTarget.stationId,
          },
        ]
      : []
  );

  // #endregion

  // #region step transitions

  function onConnectionSelect(value: TWizardConnection) {
    connection.value = value;
    stepIndex.value = 1;
  }

  function onDeviceSelect(value: IWizardDevice) {
    device.value = value;
    stepIndex.value = 2;
  }

  function onPaperSelect(value: IWizardPaperChoice) {
    paper.value = value;
    codepage.value = DEFAULT_CODEPAGE;
    stepIndex.value = 3;
  }

  function onTestConfirmed() {
    stepIndex.value = 4;
  }

  function back() {
    if (stepIndex.value === 0) return;
    // Re-choosing the device drops the previous just-added manual entry.
    if (stepIndex.value === 2) void cleanupAddedDevice();
    stepIndex.value -= 1;
  }

  // #endregion

  // #region finish + cleanup

  async function onFinish(result: IWizardAssignResult) {
    const currentDevice = device.value;
    const currentPaper = paper.value;
    if (!currentDevice || !currentPaper || finishing.value) return;

    finishing.value = true;
    let closed = false;
    try {
      const committed = await v2Store.mutateConfig((draft) => {
        const nextId =
          draft.logicalPrinters.reduce((max, lp) => Math.max(max, lp.id), 0) +
          1;
        draft.logicalPrinters.push({
          id: nextId,
          name: result.name,
          ref: currentDevice.ref,
          transport: currentDevice.transport,
          format: currentPaper.format,
          paperWidthMm: currentPaper.paperWidthMm,
          codepage: codepage.value,
          // `model` is also the new-build marker the projection gates the
          // width table / QR sizing on (paperWidthMm is dormant on configs
          // that predate the per-printer settings model) — every new-build
          // save must write it.
          ...(currentPaper.format === 'escpos'
            ? { model: DEFAULT_ESCPOS_SETTINGS.model }
            : {}),
          // Etiquetas printers materialize explicit zpl from the RESOLVED
          // chain (explicit-by-ref → legacy localStorage stickerSettings →
          // defaults), never from DEFAULT_ZPL_SETTINGS: adding a printer
          // whose labels were tuned through the legacy sticker modal must
          // adopt that geometry, not stomp it. printerSettingsOf returns
          // freshly-normalized clones (bridge mode resolves by ref — only
          // `id` matters here, same synthetic-target shape as
          // printerTargetOf in use-printing-v2-ui).
          ...(currentPaper.format === 'zpl'
            ? {
                zpl: printingStore.printerSettingsOf({
                  id: currentDevice.ref,
                  name: result.name,
                  type: 'usb',
                }).zpl,
              }
            : {}),
        });
        applyAssignmentSelection(draft, nextId, result.selection);

        // Resolving a v1 reassign card removes its unresolved entry.
        if (props.preTarget) {
          draft.unresolvedV1 = draft.unresolvedV1.filter(
            (entry) =>
              !(
                entry.destination === props.preTarget!.destination &&
                entry.stationId === props.preTarget!.stationId &&
                entry.v1Name === props.preTarget!.v1Name
              )
          );
        }
      });

      if (committed) {
        finished = true;
        ui.showToast(AlertType.Success, i18nT('printingV2.wizardDone'));
        // Renderer-safety: let the commit's re-renders flush BEFORE starting
        // the modal's leave transition, and never mutate local reactive state
        // after emit('close') begins the teardown.
        await nextTick();
        closed = true;
        emit('close');
      }
    } finally {
      if (!closed) finishing.value = false;
    }
  }

  /**
   * Cancel cleanup: a manual (tcp/serial-bt) entry the wizard just created is
   * removed from the bridge UNLESS an existing logical printer references the
   * same ref (refs are deterministic — re-adding an already-configured
   * printer and cancelling must not delete it out from under its config).
   */
  async function cleanupAddedDevice() {
    const currentDevice = device.value;
    device.value = null;
    if (!currentDevice?.addedByWizard) return;

    const referenced = (v2Store.config?.logicalPrinters ?? []).some(
      (lp) => lp.ref === currentDevice.ref
    );
    if (referenced) return;

    try {
      await bridge.value?.removePrinter(currentDevice.ref);
    } catch (e) {
      console.error('[PrintingV2] wizard cleanup failed', e);
    }
  }

  onBeforeUnmount(() => {
    if (!finished) void cleanupAddedDevice();
  });

  // #endregion
</script>

<template>
  <div class="v2-printer-setup-wizard">
    <div class="wizard-header">
      <h3 class="wizard-title">{{ $t('printingV2.wizardTitle') }}</h3>
      <!-- Disabled while committing: closing mid-commit would run the
           unmount cleanup and delete the just-added device out from under
           the in-flight config write. -->
      <button
        class="wizard-close"
        type="button"
        :disabled="finishing"
        @click="emit('close')"
      >
        <PosUiIcon name="close" :size="20" />
      </button>
    </div>

    <div class="wizard-steps-bar">
      <V2WizardSteps :steps="stepLabels" :current="stepIndex" />
    </div>

    <div class="wizard-body">
      <StepConnection
        v-if="currentStep === 'connection'"
        @select="onConnectionSelect"
      />
      <StepDevice
        v-else-if="currentStep === 'device' && connection"
        :connection="connection"
        @select="onDeviceSelect"
      />
      <StepPaper v-else-if="currentStep === 'paper'" @select="onPaperSelect" />
      <StepTest
        v-else-if="currentStep === 'test' && device && paper"
        :device="device"
        :format="paper.format"
        :paper-width-mm="paper.paperWidthMm"
        :codepage="codepage"
        @confirmed="onTestConfirmed"
        @codepage-change="codepage = $event"
      />
      <StepAssign
        v-else-if="currentStep === 'assign' && device"
        :default-name="device.name"
        :pre-selection="preSelection"
        :finishing="finishing"
        @finish="onFinish"
      />
    </div>

    <div class="wizard-footer">
      <PosUiButton
        v-if="stepIndex > 0"
        color="neutral"
        variant="ghost"
        icon="arrow-left"
        :disabled="finishing"
        @click="back"
      >
        {{ $t('printingV2.back') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-printer-setup-wizard {
    @apply tw-flex tw-flex-col tw-bg-white;

    .wizard-header {
      @apply tw-flex tw-items-center tw-justify-between tw-border-b tw-border-neutral-100 tw-px-5 tw-py-4;

      .wizard-title {
        @apply tw-m-0 tw-text-base tw-font-bold tw-text-neutral-900;
      }

      .wizard-close {
        @apply tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .wizard-steps-bar {
      @apply tw-px-5 tw-pt-4;
    }

    .wizard-body {
      @apply tw-min-h-[280px] tw-px-5 tw-py-4;
    }

    .wizard-footer {
      @apply tw-flex tw-min-h-[56px] tw-items-center tw-border-t tw-border-neutral-100 tw-px-5 tw-py-2;
    }
  }
</style>
